import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ProductType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import { useToast } from 'context/ToastContext';
import SvgArchive from 'components/svg/SvgArchive';
import SvgCheck from 'components/svg/SvgCheck';
import SvgClipCopy from 'components/svg/svgClipCopy';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import { useProduct } from './context/ProductProvider';
import useDeleteProduct from './hooks/hookDeleteProduct';

interface ProductTableRowActionsProps {
    product: ProductType;
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const ProductTableRowActions = ({ product, fetchProducts }: ProductTableRowActionsProps) => {
    const { showToast } = useToast();
    const { t } = useTranslation(['Products', 'common']);
    const { setShowEditProduct, setShowSpinner } = useProduct();
    const { deleteProduct, archiveProduct } = useDeleteProduct();

    const cbDeleteProduct = async () => {
        setShowSpinner(true);
        const resActive = await apiProduct.deleteProduct(product.ID);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchProducts(true);
        setShowSpinner(false);
    };

    const cbArchiveProduct = async () => {
        setShowSpinner(true);
        const resActive = await apiProduct.setStat([product.ID], 0);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchProducts(true);
        setShowSpinner(false);
    };

    const handleClickActive = async () => {
        setShowSpinner(true);
        const resActive = await apiProduct.setStat([product.ID], 1);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchProducts(true);
        setShowSpinner(false);
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('prodPopup.edit'),
                t('prodPopup.editHint'),
                () => setShowEditProduct(product.ID),
            )}

            {product.prodStatus === 1
                ? renderMenuItem(
                      <SvgArchive />,
                      t('prodPopup.archive'),
                      t('prodPopup.archiveHint'),
                      () => archiveProduct(cbArchiveProduct),
                  )
                : renderMenuItem(
                      <SvgCheck />,
                      t('prodPopup.active'),
                      t('prodPopup.activeHint'),
                      () => {
                          void handleClickActive();
                      },
                  )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgClipCopy />,
                t('prodPopup.clone'),
                t('prodPopup.cloneHint'),
                () => setShowEditProduct(-product.ID),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('prodPopup.delete'),
                t('prodPopup.deleteHint'),
                () => deleteProduct(cbDeleteProduct),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-product-${product.ID}`}
            >
                <i className="bi bi-three-dots"></i>
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ProductTableRowActions;
